import React, { useEffect, useMemo, useState } from 'react';
import HotelOutlinedIcon from '@mui/icons-material/HotelOutlined';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useTranslation } from 'react-i18next';

import BasicCli from '../../../main/components/basicCli/BasicCli';
import axiosInstance from '../../../main/initializers/axios';
import { ApiConstants } from '../../../main/constants/ApiConstants';
import IHotel from '../../../main/interfaces/IHotel';
import IRoom from '../../../main/interfaces/IRoom';
import useGetUser from '../../../main/hooks/useGetUser';
import { getCompanyTimeZone } from '../../../main/utils/timezone';
import '../Hotel/Hotel.css';
import '../HotelShared/HotelShared.css';
import './HotelDashboard.css';

type MetricCardProps = {
  title: string;
  value: string;
  hint: string;
  tone?: 'mint' | 'sky' | 'amber' | 'peach';
  icon: React.ReactNode;
};

type DailySeriesItem = {
  label: string;
  booked: number;
  cancelled: number;
};

type MonthlyRevenueItem = {
  label: string;
  revenue: number;
};

type UpcomingArrivalItem = {
  reservationId: number;
  guestName: string;
  roomNumber: string;
  checkInDate: string;
  lockedPriceTotal: number;
};

type SourceBreakdownItem = {
  label: string;
  value: number;
};

type CalendarMode = 'week' | 'month' | 'year';

type CalendarReservationItem = {
  id: number;
  hotelId?: number;
  roomId: number;
  roomNumber?: string;
  checkInDate: string;
  checkOutDate: string;
  status: number;
  guestName?: string;
};

type CalendarColumn = {
  key: string;
  label: string;
  shortLabel: string;
  rangeStart: Date;
  rangeEnd: Date;
  isToday: boolean;
};

type HotelDashboardResponse = {
  hotelId: number | null;
  hotelName: string;
  generatedAt: string;
  totalRooms: number;
  occupiedRooms: number;
  reservedRooms: number;
  availableRooms: number;
  outOfServiceRooms: number;
  occupancyRate: number;
  checkInsToday: number;
  checkInsYesterday: number;
  checkOutsToday: number;
  checkOutsYesterday: number;
  totalRevenue: number;
  revenueCurrentWeek: number;
  revenuePreviousWeek: number;
  pendingRequests: number;
  activeReservations: number;
  totalBookingsThisWeek: number;
  totalBookingsPreviousWeek: number;
  monthlyRevenue: MonthlyRevenueItem[];
  dailyReservations: DailySeriesItem[];
  sourceBreakdown: SourceBreakdownItem[];
  upcomingArrivals: UpcomingArrivalItem[];
};

const normalizeMonthlyRevenue = (items: any[]): MonthlyRevenueItem[] =>
  items.map((item) => ({
    label: String(item?.label ?? item?.Label ?? ''),
    revenue: Number(item?.revenue ?? item?.Revenue ?? 0),
  }))
  .filter((item) => item.label);

const normalizeDailyReservations = (items: any[]): DailySeriesItem[] =>
  items.map((item) => ({
    label: String(item?.label ?? item?.Label ?? ''),
    booked: Number(item?.booked ?? item?.Booked ?? 0),
    cancelled: Number(item?.cancelled ?? item?.Cancelled ?? 0),
  }))
  .filter((item) => item.label);

const normalizeSourceBreakdown = (items: any[]): SourceBreakdownItem[] =>
  items.map((item) => ({
    label: String(item?.label ?? item?.Label ?? ''),
    value: Number(item?.value ?? item?.Value ?? 0),
  }))
  .filter((item) => item.label);

const normalizeUpcomingArrivals = (items: any[]): UpcomingArrivalItem[] =>
  items.map((item) => ({
    reservationId: Number(item?.reservationId ?? item?.ReservationId ?? 0),
    guestName: String(item?.guestName ?? item?.GuestName ?? ''),
    roomNumber: String(item?.roomNumber ?? item?.RoomNumber ?? ''),
    checkInDate: String(item?.checkInDate ?? item?.CheckInDate ?? ''),
    lockedPriceTotal: Number(item?.lockedPriceTotal ?? item?.LockedPriceTotal ?? 0),
  }))
  .filter((item) => item.reservationId > 0);

const formatCompactNumber = (value: number) =>
  new Intl.NumberFormat(undefined, {
    notation: 'compact',
    maximumFractionDigits: value >= 100 ? 0 : 1,
  }).format(value);

const formatMoney = (value: number) =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);

const formatMoneyWithCurrency = (value: number, currencySymbol?: string) => `${formatMoney(value)}${currencySymbol ? ` ${currencySymbol}` : ''}`;

const formatPercent = (value: number) => `${value.toFixed(1)}%`;
const getBarHeight = (value: number, maxValue: number) => (value > 0 ? Math.max((value / maxValue) * 130, 8) : 0);

const getMonthLabel = (monthKey: string) => {
  const [year, month] = monthKey.split('-');
  const date = new Date(Number(year), Number(month) - 1, 1);
  return date.toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
};

const startOfDay = (value: Date | string) => {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
};

const startOfWeek = (value: Date) => {
  const date = startOfDay(value);
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  return date;
};

const endOfWeek = (value: Date) => {
  const date = startOfWeek(value);
  date.setDate(date.getDate() + 6);
  return date;
};

const startOfMonth = (value: Date) => {
  const date = startOfDay(value);
  date.setDate(1);
  return date;
};

const endOfMonth = (value: Date) => {
  const date = startOfMonth(value);
  date.setMonth(date.getMonth() + 1);
  date.setDate(0);
  return date;
};

const startOfYear = (value: Date) => new Date(value.getFullYear(), 0, 1);

const endOfYear = (value: Date) => new Date(value.getFullYear(), 11, 31);

const addDays = (value: Date, amount: number) => {
  const date = startOfDay(value);
  date.setDate(date.getDate() + amount);
  return date;
};

const formatLocalDateKey = (value: Date) => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDateKeyByTimeZone = (value: Date, timeZone: string) => {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  const parts = formatter.formatToParts(value);
  const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
};

const formatCalendarCellDay = (value: Date) => value.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric' });

const formatCalendarHeaderDate = (value: Date) => value.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });

const formatCalendarMonthCell = (value: Date) => value.toLocaleDateString(undefined, { month: 'short' });

const resolveGuestName = (item: any) => {
  const directName = item?.guestName ?? item?.GuestName ?? item?.primaryGuestName ?? item?.PrimaryGuestName;

  if (typeof directName === 'string' && directName.trim()) {
    return directName.trim();
  }

  const nestedGuest = item?.guest ?? item?.Guest ?? item?.primaryGuest ?? item?.PrimaryGuest;
  const firstName = nestedGuest?.firstName ?? nestedGuest?.FirstName ?? '';
  const lastName = nestedGuest?.lastName ?? nestedGuest?.LastName ?? '';
  const fullName = `${firstName} ${lastName}`.trim();

  if (fullName) {
    return fullName;
  }

  const guestList = Array.isArray(item?.guests) ? item.guests : Array.isArray(item?.Guests) ? item.Guests : [];

  const primaryGuest = guestList.find((guest: any) => guest?.isPrimary || guest?.IsPrimary) ?? guestList[0];
  if (!primaryGuest) return '';

  const guestItem = primaryGuest?.guest ?? primaryGuest?.Guest ?? primaryGuest;
  return `${guestItem?.firstName ?? guestItem?.FirstName ?? ''} ${guestItem?.lastName ?? guestItem?.LastName ?? ''}`.trim();
};

const buildDonutSegments = (values: number[]) => {
  const total = values.reduce((sum, value) => sum + value, 0);
  if (total <= 0) return [];

  let current = -90;
  return values.map((value) => {
    const angle = (value / total) * 360;
    const segment = { value, start: current, end: current + angle };
    current += angle;
    return segment;
  });
};

const polarToCartesian = (cx: number, cy: number, radius: number, angle: number) => {
  const angleInRadians = ((angle - 90) * Math.PI) / 180;
  return {
    x: cx + radius * Math.cos(angleInRadians),
    y: cy + radius * Math.sin(angleInRadians),
  };
};

const describeArc = (cx: number, cy: number, radius: number, startAngle: number, endAngle: number) => {
  const start = polarToCartesian(cx, cy, radius, endAngle);
  const end = polarToCartesian(cx, cy, radius, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
  return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`;
};

const MetricCard = ({ title, value, hint, tone = 'mint', icon }: MetricCardProps) => (
  <article className={`hotelDashMetricCard tone-${tone}`}>
    <div className="hotelDashMetricTop">
      <div>
        <span className="hotelDashMetricTitle">{title}</span>
        <strong className="hotelDashMetricValue">{value}</strong>
      </div>
      <span className="hotelDashMetricIcon">{icon}</span>
    </div>
    <span className="hotelDashMetricHint">{hint}</span>
  </article>
);

const initialDashboardData: HotelDashboardResponse = {
  hotelId: null,
  hotelName: '',
  generatedAt: '',
  totalRooms: 0,
  occupiedRooms: 0,
  reservedRooms: 0,
  availableRooms: 0,
  outOfServiceRooms: 0,
  occupancyRate: 0,
  checkInsToday: 0,
  checkInsYesterday: 0,
  checkOutsToday: 0,
  checkOutsYesterday: 0,
  totalRevenue: 0,
  revenueCurrentWeek: 0,
  revenuePreviousWeek: 0,
  pendingRequests: 0,
  activeReservations: 0,
  totalBookingsThisWeek: 0,
  totalBookingsPreviousWeek: 0,
  monthlyRevenue: [],
  dailyReservations: [],
  sourceBreakdown: [],
  upcomingArrivals: [],
};

const HotelDashboard: React.FC = () => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const user = useGetUser();
  const preferredHotelId = Number((location.state as any)?.selectedHotelId ?? 0);

  const [loading, setLoading] = useState(true);
  const [hotels, setHotels] = useState<IHotel[]>([]);
  const [selectedHotelId, setSelectedHotelId] = useState<number | null>(null);
  const [calendarMode, setCalendarMode] = useState<CalendarMode>('week');
  const [calendarCursorDate] = useState<Date>(() => startOfDay(new Date()));
  const [calendarRooms, setCalendarRooms] = useState<IRoom[]>([]);
  const [calendarReservations, setCalendarReservations] = useState<CalendarReservationItem[]>([]);
  const [dashboardData, setDashboardData] = useState<HotelDashboardResponse>(initialDashboardData);

  useEffect(() => {
    const loadHotels = async () => {
      try {
        setLoading(true);
        setSelectedHotelId(null);
        setDashboardData(initialDashboardData);

        const hotelsRes = await axiosInstance.get(ApiConstants.hotel.hotels);
        const hotelItems = Array.isArray(hotelsRes.data) ? hotelsRes.data : [];
        setHotels(hotelItems);

        if (hotelItems.length === 0) {
          setSelectedHotelId(null);
          setLoading(false);
          return;
        }

        setSelectedHotelId((current) => {
          if (current && hotelItems.some((hotel: IHotel) => Number(hotel.id) === Number(current))) return current;
          if (preferredHotelId > 0 && hotelItems.some((hotel: IHotel) => Number(hotel.id) === preferredHotelId)) return preferredHotelId;

          return hotelItems[0]?.id ?? null;
        });
      } catch (error: any) {
        toast.error(error?.response?.data || i18n.t('hotelDashboard.messages.loadHotelsError', { defaultValue: 'Failed to load hotels.' }));
        setLoading(false);
      }
    };

    loadHotels();
  }, [i18n, preferredHotelId]);

  useEffect(() => {
    const loadDashboardData = async () => {
      if (!selectedHotelId) {
        setDashboardData(initialDashboardData);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await axiosInstance.get(ApiConstants.hotel.dashboard, {
          params: { hotelId: selectedHotelId },
        });

        const data = response.data ?? {};
        setDashboardData({
          hotelId: Number(data.hotelId ?? data.HotelId ?? selectedHotelId),
          hotelName: data.hotelName ?? data.HotelName ?? '',
          generatedAt: data.generatedAt ?? data.GeneratedAt ?? '',
          totalRooms: Number(data.totalRooms ?? data.TotalRooms ?? 0),
          occupiedRooms: Number(data.occupiedRooms ?? data.OccupiedRooms ?? 0),
          reservedRooms: Number(data.reservedRooms ?? data.ReservedRooms ?? 0),
          availableRooms: Number(data.availableRooms ?? data.AvailableRooms ?? 0),
          outOfServiceRooms: Number(data.outOfServiceRooms ?? data.OutOfServiceRooms ?? 0),
          occupancyRate: Number(data.occupancyRate ?? data.OccupancyRate ?? 0),
          checkInsToday: Number(data.checkInsToday ?? data.CheckInsToday ?? 0),
          checkInsYesterday: Number(data.checkInsYesterday ?? data.CheckInsYesterday ?? 0),
          checkOutsToday: Number(data.checkOutsToday ?? data.CheckOutsToday ?? 0),
          checkOutsYesterday: Number(data.checkOutsYesterday ?? data.CheckOutsYesterday ?? 0),
          totalRevenue: Number(data.totalRevenue ?? data.TotalRevenue ?? 0),
          revenueCurrentWeek: Number(data.revenueCurrentWeek ?? data.RevenueCurrentWeek ?? 0),
          revenuePreviousWeek: Number(data.revenuePreviousWeek ?? data.RevenuePreviousWeek ?? 0),
          pendingRequests: Number(data.pendingRequests ?? data.PendingRequests ?? 0),
          activeReservations: Number(data.activeReservations ?? data.ActiveReservations ?? 0),
          totalBookingsThisWeek: Number(data.totalBookingsThisWeek ?? data.TotalBookingsThisWeek ?? 0),
          totalBookingsPreviousWeek: Number(data.totalBookingsPreviousWeek ?? data.TotalBookingsPreviousWeek ?? 0),
          monthlyRevenue: normalizeMonthlyRevenue(
            Array.isArray(data.monthlyRevenue ?? data.MonthlyRevenue) ? (data.monthlyRevenue ?? data.MonthlyRevenue) : [],
          ),
          dailyReservations: normalizeDailyReservations(
            Array.isArray(data.dailyReservations ?? data.DailyReservations) ? (data.dailyReservations ?? data.DailyReservations) : [],
          ),
          sourceBreakdown: normalizeSourceBreakdown(
            Array.isArray(data.sourceBreakdown ?? data.SourceBreakdown) ? (data.sourceBreakdown ?? data.SourceBreakdown) : [],
          ),
          upcomingArrivals: normalizeUpcomingArrivals(
            Array.isArray(data.upcomingArrivals ?? data.UpcomingArrivals) ? (data.upcomingArrivals ?? data.UpcomingArrivals) : [],
          ),
        });
      } catch (error: any) {
        toast.error(
          error?.response?.data || i18n.t('hotelDashboard.messages.loadDashboardError', { defaultValue: 'Failed to load hotel dashboard.' }),
        );
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [i18n, selectedHotelId]);

  useEffect(() => {
    const loadCalendarData = async () => {
      if (!selectedHotelId) {
        setCalendarRooms([]);
        setCalendarReservations([]);
        return;
      }

      try {
        const [roomsResponse, reservationsResponse] = await Promise.all([
          axiosInstance.get(ApiConstants.hotel.rooms, {
            params: { hotelId: selectedHotelId },
          }),
          axiosInstance.post(ApiConstants.reservation.listCt, {
            hotelId: selectedHotelId,
            gridItems: [],
            pagination: {
              page: 1,
              itemPerPage: 1000,
              count: 0,
              itemsPerPage: [10, 20, 50, 100],
            },
          }),
        ]);

        const allRooms = Array.isArray(roomsResponse.data) ? roomsResponse.data : [];
        const reservationPayload = reservationsResponse.data;
        const reservationItems = Array.isArray(reservationPayload)
          ? reservationPayload
          : Array.isArray(reservationPayload?.items)
            ? reservationPayload.items
            : [];

        setCalendarRooms(
          allRooms
            .filter((room: IRoom) => Number(room.hotelId) === Number(selectedHotelId))
            .sort((left: IRoom, right: IRoom) => {
              const floorCompare = Number(left.floor ?? 0) - Number(right.floor ?? 0);
              if (floorCompare !== 0) return floorCompare;
              return String(left.roomNumber ?? '').localeCompare(String(right.roomNumber ?? ''), undefined, { numeric: true });
            }),
        );

        setCalendarReservations(
          reservationItems
            .filter((item: any) => Number(item.hotelId ?? item.HotelId ?? 0) === Number(selectedHotelId))
            .filter((item: any) => ![4, 5].includes(Number(item.status ?? item.Status ?? 0)))
            .map((item: any) => ({
              id: Number(item.id ?? item.Id ?? 0),
              hotelId: Number(item.hotelId ?? item.HotelId ?? selectedHotelId),
              roomId: Number(item.roomId ?? item.RoomId ?? 0),
              roomNumber: item.roomNumber ?? item.RoomNumber ?? '',
              checkInDate: item.checkInDate ?? item.CheckInDate ?? '',
              checkOutDate: item.checkOutDate ?? item.CheckOutDate ?? '',
              status: Number(item.status ?? item.Status ?? 0),
              guestName: resolveGuestName(item),
            })),
        );
      } catch (error: any) {
        toast.error(
          error?.response?.data || i18n.t('hotelDashboard.messages.loadCalendarError', { defaultValue: 'Failed to load reservation calendar.' }),
        );
      }
    };

    loadCalendarData();
  }, [i18n, selectedHotelId]);

  const companyTimeZone = getCompanyTimeZone(user);
  const todayDateKey = formatDateKeyByTimeZone(new Date(), companyTimeZone);

  const calendarPeriod = useMemo(() => {
    if (calendarMode === 'week') {
      const start = startOfWeek(calendarCursorDate);
      const columns: CalendarColumn[] = Array.from({ length: 7 }, (_, index) => {
        const date = addDays(start, index);
        return {
          key: formatLocalDateKey(date),
          label: formatCalendarCellDay(date),
          shortLabel: date.toLocaleDateString(undefined, { weekday: 'short' }),
          rangeStart: startOfDay(date),
          rangeEnd: startOfDay(date),
          isToday: formatDateKeyByTimeZone(date, companyTimeZone) === todayDateKey,
        };
      });

      return {
        start,
        end: endOfWeek(calendarCursorDate),
        columns,
      };
    }

    if (calendarMode === 'month') {
      const start = startOfMonth(calendarCursorDate);
      const end = endOfMonth(calendarCursorDate);
      const totalDays = end.getDate();
      const columns: CalendarColumn[] = Array.from({ length: totalDays }, (_, index) => {
        const date = addDays(start, index);
        return {
          key: formatLocalDateKey(date),
          label: formatCalendarCellDay(date),
          shortLabel: String(date.getDate()),
          rangeStart: startOfDay(date),
          rangeEnd: startOfDay(date),
          isToday: formatDateKeyByTimeZone(date, companyTimeZone) === todayDateKey,
        };
      });

      return {
        start,
        end,
        columns,
      };
    }

    const start = startOfYear(calendarCursorDate);
    const end = endOfYear(calendarCursorDate);
    const columns: CalendarColumn[] = Array.from({ length: 12 }, (_, index) => {
      const monthStart = new Date(start.getFullYear(), index, 1);
      const monthEnd = endOfMonth(monthStart);
      return {
        key: `${monthStart.getFullYear()}-${String(monthStart.getMonth() + 1).padStart(2, '0')}`,
        label: formatCalendarMonthCell(monthStart),
        shortLabel: formatCalendarMonthCell(monthStart),
        rangeStart: startOfDay(monthStart),
        rangeEnd: startOfDay(monthEnd),
        isToday:
          formatDateKeyByTimeZone(monthStart, companyTimeZone).slice(0, 7) === todayDateKey.slice(0, 7),
      };
    });

    return {
      start,
      end,
      columns,
    };
  }, [calendarCursorDate, calendarMode, companyTimeZone, todayDateKey]);

  const calendarSummaryCells = useMemo(() => {
    return calendarPeriod.columns.map((column) => {
      const reservations = calendarReservations.filter((reservation) => {
        const reservationStart = startOfDay(reservation.checkInDate);
        const reservationEnd = addDays(startOfDay(reservation.checkOutDate), -1);
        return reservationStart <= column.rangeEnd && reservationEnd >= column.rangeStart;
      });

      const occupied = reservations.filter((reservation) => reservation.status === 2).length;
      const reserved = reservations.filter((reservation) => reservation.status !== 2).length;
      const busy = occupied + reserved;
      const available = Math.max(calendarRooms.length - busy, 0);
      const occupancyRate = calendarRooms.length > 0 ? (busy / calendarRooms.length) * 100 : 0;

      let tone = 'free';
      if (busy > 0 && occupancyRate >= 70) tone = 'occupied';
      else if (busy > 0) tone = 'reserved';

      return {
        ...column,
        occupied,
        reserved,
        busy,
        available,
        occupancyRate,
        tone,
      };
    });
  }, [calendarPeriod.columns, calendarReservations, calendarRooms.length]);

  const calendarRoomGroups = useMemo(() => {
    const grouped = calendarRooms.reduce<Record<string, IRoom[]>>((acc, room) => {
      const groupName = room.roomType?.name || (room as any)?.roomTypeName || `Floor ${room.floor || 0}`;

      if (!acc[groupName]) {
        acc[groupName] = [];
      }

      acc[groupName].push(room);
      return acc;
    }, {});

    return Object.entries(grouped).map(([groupName, rooms]) => {
      const rows = rooms.map((room) => {
        const bars = calendarReservations
          .filter((reservation) => Number(reservation.roomId) === Number(room.id))
          .map((reservation) => {
            const startIndex = calendarPeriod.columns.findIndex((column) => {
              const reservationStart = startOfDay(reservation.checkInDate);
              const reservationEnd = addDays(startOfDay(reservation.checkOutDate), -1);
              return reservationStart <= column.rangeEnd && reservationEnd >= column.rangeStart;
            });

            const endIndex = [...calendarPeriod.columns].reverse().findIndex((column) => {
              const reservationStart = startOfDay(reservation.checkInDate);
              const reservationEnd = addDays(startOfDay(reservation.checkOutDate), -1);
              return reservationStart <= column.rangeEnd && reservationEnd >= column.rangeStart;
            });

            if (startIndex === -1 || endIndex === -1) {
              return null;
            }

            const lastIndex = calendarPeriod.columns.length - 1 - endIndex;
            const isCheckedIn = reservation.status === 2;
            const reservationStart = startOfDay(reservation.checkInDate);
            const reservationEnd = addDays(startOfDay(reservation.checkOutDate), -1);
            const startsInPeriod = reservationStart >= calendarPeriod.start && reservationStart <= calendarPeriod.end;
            const endsInPeriod = reservationEnd >= calendarPeriod.start && reservationEnd <= calendarPeriod.end;

            return {
              id: reservation.id,
              label: reservation.guestName || `Reservation #${reservation.id}`,
              startColumn: startIndex + 1,
              endColumn: lastIndex + 1,
              tone: isCheckedIn ? 'occupied' : 'reserved',
              startsInPeriod,
              endsInPeriod,
            };
          })
          .filter(Boolean) as Array<{
          id: number;
          label: string;
          startColumn: number;
          endColumn: number;
          tone: 'occupied' | 'reserved';
          startsInPeriod: boolean;
          endsInPeriod: boolean;
        }>;

        return {
          room,
          bars,
        };
      });

      const groupAvailability = calendarPeriod.columns.map((column) => {
        const busy = calendarReservations.filter((reservation) => {
          const reservationStart = startOfDay(reservation.checkInDate);
          const reservationEnd = addDays(startOfDay(reservation.checkOutDate), -1);
          const overlaps = reservationStart <= column.rangeEnd && reservationEnd >= column.rangeStart;
          return overlaps && rooms.some((room) => Number(room.id) === Number(reservation.roomId));
        }).length;

        return Math.max(rooms.length - busy, 0);
      });

      return {
        groupName,
        roomCount: rooms.length,
        availability: groupAvailability,
        rows,
      };
    });
  }, [calendarPeriod.columns, calendarPeriod.end, calendarPeriod.start, calendarReservations, calendarRooms]);

  const calendarBusyUnits = calendarSummaryCells.reduce((sum, cell) => sum + cell.busy, 0);
  const calendarOccupiedUnits = calendarSummaryCells.reduce((sum, cell) => sum + cell.occupied, 0);
  const calendarReservedUnits = calendarSummaryCells.reduce((sum, cell) => sum + cell.reserved, 0);
  const calendarCapacityUnits = Math.max(calendarRooms.length * calendarPeriod.columns.length, 1);
  const calendarFreeUnits = Math.max(calendarCapacityUnits - calendarBusyUnits, 0);

  const revenueValues = dashboardData.monthlyRevenue.map((item) => item.revenue);
  const peakRevenue = Math.max(...revenueValues, 0);
  const revenueChartData = dashboardData.monthlyRevenue.map((item) => ({
    label: getMonthLabel(item.label),
    revenue: item.revenue,
  }));

  const reservationChartData = useMemo(() => {
    return dashboardData.dailyReservations.map((item) => ({
      label: item.label,
      arrived: item.booked,
      cancelled: item.cancelled,
    }));
  }, [dashboardData.dailyReservations]);

  const maxDailyValue = Math.max(...reservationChartData.map((item) => Math.max(item.arrived, item.cancelled)), 1);

  const sourceColors = ['#c6f1df', '#9bd4b9', '#d7eb73', '#5ca585'];
  const donutSegments = buildDonutSegments(dashboardData.sourceBreakdown.map((item) => item.value));
  const totalSources = dashboardData.sourceBreakdown.reduce((sum, item) => sum + item.value, 0);

  const bookingsDelta =
    dashboardData.totalBookingsPreviousWeek > 0
      ? ((dashboardData.totalBookingsThisWeek - dashboardData.totalBookingsPreviousWeek) / dashboardData.totalBookingsPreviousWeek) * 100
      : dashboardData.totalBookingsThisWeek > 0
        ? 100
        : 0;

  const revenueDelta =
    dashboardData.revenuePreviousWeek > 0
      ? ((dashboardData.revenueCurrentWeek - dashboardData.revenuePreviousWeek) / dashboardData.revenuePreviousWeek) * 100
      : dashboardData.revenueCurrentWeek > 0
        ? 100
        : 0;

  const currencySymbol = user?.companyCurrencySymbol ?? '';
  const calendarModeOptions: Array<{ value: CalendarMode; label: string }> = [
    { value: 'week', label: i18n.t('hotelDashboard.filters.week', { defaultValue: 'Week' }) },
    { value: 'month', label: i18n.t('hotelDashboard.filters.month', { defaultValue: 'Month' }) },
    { value: 'year', label: i18n.t('hotelDashboard.filters.year', { defaultValue: 'Year' }) },
  ];

  return (
    <BasicCli>
      <div className="hotelSectionPage hotel-dashboard hotelDashPage">
        <div className="ui-panel hotelSectionHeader hotelDashHero">
          <div className="hotelSectionHeading">
            <span className="hotelDashEyebrow">{i18n.t('hotelDashboard.eyebrow', { defaultValue: 'Hotel analytics' })}</span>
            <h1>{i18n.t('hotelDashboard.title', { defaultValue: 'Hotel Dashboard' })}</h1>
          </div>

          <div className="hotelSectionActions hotelDashHeroActions">
            <label className="hotelDashSelectField">
              <span>{i18n.t('hotel.hotel', { defaultValue: 'Hotel' })}</span>
              <select value={selectedHotelId ?? ''} onChange={(event) => setSelectedHotelId(event.target.value ? Number(event.target.value) : null)}>
                {hotels.map((hotel) => (
                  <option key={hotel.id} value={hotel.id}>
                    {hotel.name}
                  </option>
                ))}
              </select>
            </label>

            <div className="hotelDashQuickLinks">
              <Link to="/reservation" className="hotelDashQuickLink">
                {i18n.t('hotelDashboard.actions.viewReservations', { defaultValue: 'View reservations' })}
              </Link>
              <Link to="/bookingRequest" className="hotelDashQuickLink secondary">
                {i18n.t('hotelDashboard.actions.openRequests', { defaultValue: 'Open requests' })}
              </Link>
            </div>
          </div>
        </div>

        <section className="hotelDashMetrics">
          <MetricCard
            title={i18n.t('hotelDashboard.metrics.newBookings', { defaultValue: 'New bookings' })}
            value={loading ? '...' : formatCompactNumber(dashboardData.totalBookingsThisWeek)}
            hint={i18n.t('hotelDashboard.hints.last7DaysDelta', {
              defaultValue: 'Last 7 days {{value}}',
              value: `${bookingsDelta >= 0 ? '+' : ''}${formatPercent(bookingsDelta)}`,
            })}
            tone="mint"
            icon={<EventAvailableOutlinedIcon />}
          />
          <MetricCard
            title={i18n.t('hotelDashboard.metrics.checkInsToday', { defaultValue: 'Check-ins today' })}
            value={loading ? '...' : String(dashboardData.checkInsToday)}
            hint={i18n.t('hotelDashboard.hints.yesterday', { defaultValue: 'Yesterday {{value}}', value: dashboardData.checkInsYesterday })}
            tone="sky"
            icon={<LoginOutlinedIcon />}
          />
          <MetricCard
            title={i18n.t('hotelDashboard.metrics.checkOutsToday', { defaultValue: 'Check-outs today' })}
            value={loading ? '...' : String(dashboardData.checkOutsToday)}
            hint={i18n.t('hotelDashboard.hints.yesterday', { defaultValue: 'Yesterday {{value}}', value: dashboardData.checkOutsYesterday })}
            tone="peach"
            icon={<LogoutOutlinedIcon />}
          />
          <MetricCard
            title={i18n.t('hotelDashboard.metrics.totalRevenue', { defaultValue: 'Total revenue' })}
            value={loading ? '...' : formatMoneyWithCurrency(dashboardData.totalRevenue, currencySymbol)}
            hint={i18n.t('hotelDashboard.hints.last7DaysDelta', {
              defaultValue: 'Last 7 days {{value}}',
              value: `${revenueDelta >= 0 ? '+' : ''}${formatPercent(revenueDelta)}`,
            })}
            tone="amber"
            icon={<PaidOutlinedIcon />}
          />
        </section>

        <section className="hotelDashGrid hotelDashGridTop">
          <article className="bcCard hotelSectionCard hotelDashCard hotelDashAvailabilityCard">
            <div className="hotelDashCardHead">
              <div>
                <h2>{i18n.t('hotelDashboard.availability.title', { defaultValue: 'Room availability' })}</h2>
              </div>
              <span className="hotelDashBadge">
                {i18n.t('hotelDashboard.availability.occupiedRate', {
                  defaultValue: '{{value}} occupied',
                  value: formatPercent(dashboardData.occupancyRate),
                })}
              </span>
            </div>

            <div className="hotelDashAvailabilityBar">
              <span
                style={{ width: `${dashboardData.totalRooms ? (dashboardData.occupiedRooms / dashboardData.totalRooms) * 100 : 0}%` }}
                className="occupied"
              />
              <span
                style={{ width: `${dashboardData.totalRooms ? (dashboardData.reservedRooms / dashboardData.totalRooms) * 100 : 0}%` }}
                className="reserved"
              />
              <span
                style={{ width: `${dashboardData.totalRooms ? (dashboardData.availableRooms / dashboardData.totalRooms) * 100 : 0}%` }}
                className="available"
              />
              <span
                style={{ width: `${dashboardData.totalRooms ? (dashboardData.outOfServiceRooms / dashboardData.totalRooms) * 100 : 0}%` }}
                className="offline"
              />
            </div>

            <div className="hotelDashAvailabilityStats">
              <div>
                <span className="legend occupied" />
                <label>{i18n.t('generic.occupied', { defaultValue: 'Occupied' })}</label>
                <strong>{dashboardData.occupiedRooms}</strong>
              </div>
              <div>
                <span className="legend reserved" />
                <label>{i18n.t('hotelDashboard.labels.reserved', { defaultValue: 'Reserved' })}</label>
                <strong>{dashboardData.reservedRooms}</strong>
              </div>
              <div>
                <span className="legend available" />
                <label>{i18n.t('generic.available', { defaultValue: 'Available' })}</label>
                <strong>{dashboardData.availableRooms}</strong>
              </div>
              <div>
                <span className="legend offline" />
                <label>{i18n.t('hotelDashboard.labels.notReady', { defaultValue: 'Not ready' })}</label>
                <strong>{dashboardData.outOfServiceRooms}</strong>
              </div>
            </div>
          </article>

          <article className="bcCard hotelSectionCard hotelDashCard hotelDashRevenueCard">
            <div className="hotelDashCardHead">
              <div>
                <h2>{i18n.t('hotelDashboard.revenue.title', { defaultValue: 'Revenue' })}</h2>
              </div>
              <span className="hotelDashBadge accent">
                <TrendingUpOutlinedIcon fontSize="inherit" />
                {formatMoneyWithCurrency(peakRevenue, currencySymbol)}
              </span>
            </div>

            <div className="hotelDashRevenueChart">
              <div className="hotelDashRevenueChartInner">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={revenueChartData} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--divider)" />
                    <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fill: 'var(--muted)', fontSize: 12 }} />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: 'var(--muted)', fontSize: 12 }}
                      tickFormatter={(value) => `${formatCompactNumber(Number(value))}${currencySymbol ? ` ${currencySymbol}` : ''}`}
                    />
                    <Tooltip
                      contentStyle={{
                        borderRadius: '12px',
                        border: '1px solid var(--border)',
                        backgroundColor: 'rgba(255,255,255,0.98)',
                        boxShadow: 'var(--shadow-sm)',
                      }}
                      formatter={(value: number) => [
                        formatMoneyWithCurrency(Number(value), currencySymbol),
                        i18n.t('hotelDashboard.revenue.title', { defaultValue: 'Revenue' }),
                      ]}
                      labelFormatter={(label: string) => label}
                    />
                    <Line
                      type="monotone"
                      dataKey="revenue"
                      stroke="var(--secondary)"
                      strokeWidth={3}
                      dot={false}
                      activeDot={{ r: 6, fill: 'var(--secondary)', stroke: 'white', strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </article>
        </section>

        <section className="bcCard hotelSectionCard hotelDashCard hotelDashCalendarCard">
          <div className="hotelDashCardHead hotelDashCalendarHead">
            <div>
              <h2>{i18n.t('hotelDashboard.calendar.title', { defaultValue: 'Reservation calendar' })}</h2>
            </div>

            <div className="hotelDashCalendarControls">
              <div className="hotelDashSegmented">
                {calendarModeOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={calendarMode === option.value ? 'active' : ''}
                    onClick={() => setCalendarMode(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="hotelDashCalendarSummary">
            <span>
              <i className="occupied" />
              {i18n.t('hotelDashboard.labels.occupiedCount', { defaultValue: 'Occupied {{value}}', value: calendarOccupiedUnits })}
            </span>
            <span>
              <i className="reserved" />
              {i18n.t('hotelDashboard.labels.reservedCount', { defaultValue: 'Reserved {{value}}', value: calendarReservedUnits })}
            </span>
            <span>
              <i className="free" />
              {i18n.t('hotelDashboard.labels.freeCount', { defaultValue: 'Free {{value}}', value: calendarFreeUnits })}
            </span>
            <b>{i18n.t('hotelDashboard.labels.roomsCount', { defaultValue: '{{value}} rooms', value: calendarRooms.length })}</b>
          </div>

          {calendarRooms.length === 0 ? (
            <div className="hotelEmptyState hotelDashCalendarEmpty">
              {i18n.t('hotelDashboard.empty.noRooms', { defaultValue: 'No rooms found for this hotel yet.' })}
            </div>
          ) : (
            <div className="hotelDashTimelineWrap">
              <div
                className="hotelDashTimeline"
                style={{
                  gridTemplateColumns: `122px repeat(${calendarPeriod.columns.length}, minmax(${calendarMode === 'year' ? '84px' : '74px'}, 1fr))`,
                }}
              >
                <div className="hotelDashTimelineCorner">{i18n.t('reservationDesk.room', { defaultValue: 'Room' })}</div>

                {calendarPeriod.columns.map((column, index) => (
                  <div key={column.key} className={`hotelDashTimelineDate ${column.isToday ? 'today' : ''}`}>
                    <strong>{calendarMode === 'year' ? column.shortLabel : formatCalendarHeaderDate(column.rangeStart)}</strong>
                    <span>{calendarMode === 'year' ? column.rangeStart.getFullYear() : column.shortLabel}</span>
                  </div>
                ))}

                {calendarRoomGroups.map((group) => (
                  <React.Fragment key={group.groupName}>
                    <div className="hotelDashTimelineGroupLabel">{group.groupName}</div>

                    {group.availability.map((value, index) => (
                      <div key={`${group.groupName}-${calendarPeriod.columns[index].key}`} className="hotelDashTimelineGroupValue">
                        {value}
                      </div>
                    ))}

                    {group.rows.map((row) => (
                      <React.Fragment key={row.room.id}>
                        <div className="hotelDashTimelineRoom">
                          <strong>{row.room.roomNumber}</strong>
                        </div>

                        <div className="hotelDashTimelineLane" style={{ gridColumn: `2 / span ${calendarPeriod.columns.length}` }}>
                          <div className="hotelDashTimelineLaneGrid">
                            {calendarPeriod.columns.map((column) => (
                              <span key={`${row.room.id}-${column.key}`} className={column.isToday ? 'today' : ''} />
                            ))}
                          </div>

                          <div
                            className="hotelDashTimelineBars"
                            style={{
                              gridTemplateColumns: `repeat(${calendarPeriod.columns.length}, minmax(${calendarMode === 'year' ? '84px' : '74px'}, 1fr))`,
                            }}
                          >
                            {row.bars.map((bar) => (
                              <div
                                key={bar.id}
                                className={`hotelDashTimelineBar ${bar.tone} ${bar.startsInPeriod ? '' : 'trimmed-start'} ${bar.endsInPeriod ? '' : 'trimmed-end'}`}
                                style={{ gridColumn: `${bar.startColumn} / ${bar.endColumn + 1}` }}
                                title={bar.label}
                              >
                                <span>{bar.label}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
        </section>

        <section className="hotelDashGrid hotelDashGridBottom">
          <article className="bcCard hotelSectionCard hotelDashCard">
            <div className="hotelDashCardHead">
              <div>
                <h2>{i18n.t('hotelDashboard.reservations.title', { defaultValue: 'Reservations' })}</h2>
              </div>
              <span className="hotelDashBadge">{i18n.t('hotelDashboard.filters.last7Days', { defaultValue: '7 days' })}</span>
            </div>

            <div className="hotelDashBarChart">
              {reservationChartData.map((item) => (
                <div key={item.label} className="hotelDashBarColumn">
                  <div className="hotelDashBarStack">
                    <div className="hotelDashBarItem">
                      <span className="hotelDashBarValue">{item.arrived}</span>
                      <span
                        className="bar booked"
                        style={{ height: `${getBarHeight(item.arrived, maxDailyValue)}px` }}
                        title={i18n.t('hotelDashboard.labels.arrivedTitle', { defaultValue: 'Arrived: {{value}}', value: item.arrived })}
                      />
                    </div>
                    <div className="hotelDashBarItem">
                      <span className="hotelDashBarValue">{item.cancelled}</span>
                      <span
                        className="bar cancelled"
                        style={{ height: `${getBarHeight(item.cancelled, maxDailyValue)}px` }}
                        title={i18n.t('hotelDashboard.labels.cancelledTitle', { defaultValue: 'Cancelled: {{value}}', value: item.cancelled })}
                      />
                    </div>
                  </div>
                  <strong>{item.label}</strong>
                </div>
              ))}
            </div>

            <div className="hotelDashLegendRow">
              <span>
                <i className="booked" />
                {i18n.t('hotelDashboard.labels.arrived', { defaultValue: 'Arrived' })}
              </span>
              <span>
                <i className="cancelled" />
                {i18n.t('hotelDashboard.labels.cancelled', { defaultValue: 'Cancelled' })}
              </span>
            </div>
          </article>

          <article className="bcCard hotelSectionCard hotelDashCard">
            <div className="hotelDashCardHead">
              <div>
                <h2>{i18n.t('hotelDashboard.sources.title', { defaultValue: 'Booking by source' })}</h2>
              </div>
              <span className="hotelDashBadge">
                {i18n.t('hotelDashboard.labels.reservationsCount', { defaultValue: '{{value}} reservations', value: totalSources })}
              </span>
            </div>

            <div className="hotelDashSourceWrap">
              <div className="hotelDashDonut">
                <svg viewBox="0 0 160 160" aria-label={i18n.t('hotelDashboard.sources.aria', { defaultValue: 'Booking source chart' })}>
                  <circle cx="80" cy="80" r="52" className="hotelDashDonutTrack" />
                  {donutSegments.map((segment, index) => (
                    <path
                      key={`${segment.start}-${segment.end}`}
                      d={describeArc(80, 80, 52, segment.start, segment.end)}
                      stroke={sourceColors[index % sourceColors.length]}
                      strokeWidth="20"
                      fill="none"
                      strokeLinecap="round"
                    />
                  ))}
                </svg>
                <div className="hotelDashDonutCenter">
                  <strong>{totalSources}</strong>
                  <span>{i18n.t('generic.total', { defaultValue: 'Total' }).toLowerCase()}</span>
                </div>
              </div>

              <div className="hotelDashSourceList">
                {dashboardData.sourceBreakdown.length === 0 ? (
                  <div className="hotelEmptyState">
                    {i18n.t('hotelDashboard.empty.noSourceData', { defaultValue: 'No reservation source data yet.' })}
                  </div>
                ) : (
                  dashboardData.sourceBreakdown.map((item, index) => (
                    <div key={item.label} className="hotelDashSourceItem">
                      <span className="swatch" style={{ backgroundColor: sourceColors[index % sourceColors.length] }} />
                      <label>{item.label}</label>
                      <strong>{formatPercent(totalSources > 0 ? (item.value / totalSources) * 100 : 0)}</strong>
                    </div>
                  ))
                )}
              </div>
            </div>
          </article>

          <article className="bcCard hotelSectionCard hotelDashCard">
            <div className="hotelDashCardHead">
              <div>
                <h2>{i18n.t('hotelDashboard.snapshot.title', { defaultValue: 'Today snapshot' })}</h2>
              </div>
              <span className="hotelDashBadge">
                <HotelOutlinedIcon fontSize="inherit" />
                {i18n.t('hotelDashboard.labels.roomsCount', { defaultValue: '{{value}} rooms', value: dashboardData.totalRooms })}
              </span>
            </div>

            <div className="hotelDashSnapshotGrid">
              <div>
                <span>{i18n.t('hotelDashboard.snapshot.activeStays', { defaultValue: 'Active stays' })}</span>
                <strong>{dashboardData.activeReservations}</strong>
              </div>
              <div>
                <span>{i18n.t('hotelDashboard.snapshot.pendingRequests', { defaultValue: 'Pending requests' })}</span>
                <strong>{dashboardData.pendingRequests}</strong>
              </div>
              <div>
                <span>{i18n.t('hotelDashboard.snapshot.availableRooms', { defaultValue: 'Available rooms' })}</span>
                <strong>{dashboardData.availableRooms}</strong>
              </div>
              <div>
                <span>{i18n.t('hotelDashboard.snapshot.roomInventory', { defaultValue: 'Room inventory' })}</span>
                <strong>{dashboardData.totalRooms}</strong>
              </div>
            </div>
          </article>

          <article className="bcCard hotelSectionCard hotelDashCard hotelDashArrivalsCard">
            <div className="hotelDashCardHead">
              <div>
                <h2>{i18n.t('hotelDashboard.arrivals.title', { defaultValue: 'Upcoming arrivals' })}</h2>
              </div>
            </div>

            <div className="hotelDashArrivalList">
              {dashboardData.upcomingArrivals.length === 0 ? (
                <div className="hotelEmptyState">
                  {i18n.t('hotelDashboard.empty.noUpcomingArrivals', { defaultValue: 'No upcoming arrivals for this hotel.' })}
                </div>
              ) : (
                dashboardData.upcomingArrivals.map((reservation) => (
                  <div key={reservation.reservationId} className="hotelDashArrivalItem">
                    <div>
                      <strong>{reservation.guestName || i18n.t('reservationDesk.guest', { defaultValue: 'Guest' })}</strong>
                      <span>
                        {i18n.t('hotelDashboard.arrivals.roomDate', {
                          defaultValue: 'Room {{room}} - {{date}}',
                          room: reservation.roomNumber,
                          date: new Date(reservation.checkInDate).toLocaleDateString(),
                        })}
                      </span>
                    </div>
                    <b>{formatMoneyWithCurrency(reservation.lockedPriceTotal, currencySymbol)}</b>
                  </div>
                ))
              )}
            </div>
          </article>
        </section>
      </div>
    </BasicCli>
  );
};

export default HotelDashboard;
